/**
 * Views → claim → brand approval. Shared copy and the creator status card.
 */
import { Badge, Button, Card, Eyebrow, Money, StatTile } from "@ui/primitives";
import { fontSize, space } from "@design/index";
import { formatMoney, fromMinorString, type CurrencyCode } from "@contracts/money";

export interface EarningPreview {
  views: number | string | null;
  views_at?: string | null;
  cpm_gross_minor?: number | string | null;
  currency?: string | null;
  raw_minor?: number | string | null;
  cap_minor?: number | string | null;
  posted_minor?: number | string | null;
  claimable_minor?: number | string | null;
  claim_id?: string | null;
  claim_state?: string | null;
  claim_amount_minor?: number | string | null;
  can_claim?: boolean;
  blocked_reason?: string | null;
}

export function asCount(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatViews(value: number | string | null | undefined): string {
  const n = asCount(value);
  return n == null ? "-" : n.toLocaleString("en-GB");
}

export function claimTone(state: string | null | undefined): "ok" | "warn" | "danger" | "process" | "neutral" {
  if (state === "APPROVED") return "ok";
  if (state === "REQUESTED") return "process";
  if (state === "REJECTED") return "danger";
  return "neutral";
}

export function claimLabel(state: string | null | undefined, side: "creator" | "brand" = "creator"): string {
  if (state === "APPROVED") return side === "brand" ? "Approved" : "Paid to your earnings";
  if (state === "REQUESTED") return side === "brand" ? "Needs review" : "With the brand";
  if (state === "REJECTED") return "Claim declined";
  return "Not claimed";
}

export function blockedCopy(reason: string | null | undefined): string {
  switch (reason) {
    case "NOT_APPROVED":
      return "The brand has to approve this post before views can be claimed.";
    case "NO_VIEWS":
      return "Counted views have not come in yet. Refresh once the post has been live.";
    case "BELOW_MILESTONE":
      return "You can claim after this post reaches 1,000 counted views.";
    case "CLAIM_OPEN":
      return "Your claim is with the brand. You will see the amount here when they decide.";
    case "CAPPED":
      return "This campaign’s creator cap is already used. Extra views do not add more.";
    default:
      return "This post is not ready to claim yet.";
  }
}

export function EarningStatusCard({
  earning, currency, busy, onRefresh, onClaim,
}: {
  earning?: EarningPreview | null;
  currency: string;
  busy?: boolean;
  onRefresh?: () => void;
  onClaim?: () => void;
}) {
  const ccy = (earning?.currency ?? currency) as CurrencyCode;
  const views = asCount(earning?.views);
  const claimable = asCount(earning?.claimable_minor) ?? 0;
  const posted = asCount(earning?.posted_minor) ?? 0;
  const cap = asCount(earning?.cap_minor) ?? 0;
  const canClaim = Boolean(earning?.can_claim);
  const state = earning?.claim_state ?? null;

  return (
    <Card padding={20} style={{
      display: "grid", gap: space(3),
      background: "linear-gradient(180deg, color-mix(in srgb, var(--accent) 8%, var(--surface)), var(--surface))",
      borderColor: "var(--line)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <Eyebrow>Earning</Eyebrow>
        <Badge tone={claimTone(state)}>{claimLabel(state)}</Badge>
      </div>

      <div style={{ display: "grid", gap: space(2), gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
        <StatTile label="Counted views" {...(views != null && views >= 1000 ? { tone: "ok" as const } : {})}
          value={formatViews(views)}
          hint={views == null ? "Waiting on Zernio" : views < 1000 ? `${(1000 - views).toLocaleString("en-GB")} to go` : "Milestone reached"} />
        <StatTile label="Ready to claim" {...(canClaim ? { tone: "ok" as const } : {})}
          value={<Money value={fromMinorString(String(claimable), ccy)} emphasis={canClaim} />}
          hint="Rate × views, stops at your cap" />
        <StatTile label="Already posted"
          value={<Money value={fromMinorString(String(posted), ccy)} />}
          hint={cap > 0 ? `Cap ${formatMoney(fromMinorString(String(cap), ccy))}` : "After brand approval"} />
      </div>

      <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)", lineHeight: 1.6 }}>
        Earnings start after counted views, not when the brand approves the video.
        Claim at 1,000 views. The amount cannot pass your campaign cap even if the
        counter keeps climbing.
      </p>

      {!canClaim && earning?.blocked_reason ? (
        <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--text-2)", lineHeight: 1.55 }}>
          {blockedCopy(earning.blocked_reason)}
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {onRefresh ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={onRefresh}>
            {busy ? "Refreshing…" : "Refresh views"}
          </Button>
        ) : null}
        <Button size="sm" variant="primary" disabled={busy || !canClaim} onClick={onClaim}>
          {canClaim
            ? `Claim ${formatMoney(fromMinorString(String(claimable), ccy))}`
            : "Claim earnings"}
        </Button>
      </div>
    </Card>
  );
}
